export type OccasionType =
  | "birthday"
  | "wedding_anniversary"
  | "work_anniversary"
  | "new_baby"
  | "graduation"
  | "bereavement"
  | "special_date";

export interface OccasionConfig {
  label: string;
  emoji: string;
  recurring: boolean | null; // null = user-defined (special_date)
  tones: string[];
  reminderCopy: (name: string, label?: string) => string;
  oneTime: boolean | null; // null = user-defined (special_date)
}

export const OCCASION_TYPES: Record<OccasionType, OccasionConfig> = {
  birthday: {
    label: "Birthday",
    emoji: "🎂",
    recurring: true,
    tones: ["heartfelt", "humorous", "professional"],
    reminderCopy: (name) => `${name}'s birthday`,
    oneTime: false,
  },
  wedding_anniversary: {
    label: "Wedding Anniversary",
    emoji: "💍",
    recurring: true,
    tones: ["heartfelt", "humorous", "professional"],
    reminderCopy: (name) => `${name}'s wedding anniversary`,
    oneTime: false,
  },
  work_anniversary: {
    label: "Work Anniversary",
    emoji: "💼",
    recurring: true,
    tones: ["heartfelt", "professional"],
    reminderCopy: (name) => `${name}'s work anniversary`,
    oneTime: false,
  },
  new_baby: {
    label: "New Baby",
    emoji: "👶",
    recurring: false,
    tones: ["heartfelt", "humorous"],
    reminderCopy: (name) => `${name} has a new baby`,
    oneTime: true,
  },
  graduation: {
    label: "Graduation",
    emoji: "🎓",
    recurring: false,
    tones: ["heartfelt", "humorous", "professional"],
    reminderCopy: (name) => `${name}'s graduation`,
    oneTime: true,
  },
  bereavement: {
    label: "Loss / Bereavement",
    emoji: "🕯️",
    recurring: true,
    tones: ["heartfelt"],
    reminderCopy: (name) => `A day to remember with ${name}`,
    oneTime: false,
  },
  special_date: {
    label: "Special Date",
    emoji: "✨",
    recurring: null,
    tones: ["heartfelt", "humorous", "professional"],
    reminderCopy: (name, label) => `${name} — ${label || "Special Date"}`,
    oneTime: null,
  },
};

export const OCCASION_TYPE_KEYS = Object.keys(OCCASION_TYPES) as OccasionType[];

export const REPEATABLE_OCCASIONS: Set<OccasionType> = new Set(["new_baby", "bereavement", "special_date"]);

/**
 * Returns whether an occasion type is recurring by default.
 * For special_date, returns the user's choice.
 */
export function isOccasionRecurring(type: OccasionType, userChoice?: boolean): boolean {
  const config = OCCASION_TYPES[type];
  if (config.recurring === null) return userChoice ?? true;
  return config.recurring;
}

/**
 * Returns whether year is required for the date picker.
 * One-time occasions require year. Recurring occasions have optional year.
 */
export function isYearRequired(type: OccasionType, userRecurring?: boolean): boolean {
  const config = OCCASION_TYPES[type];
  if (config.oneTime === null) return !(userRecurring ?? true); // special_date: required if not recurring
  return config.oneTime === true; // one-time occasions require year
}
